import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calculator } from "lucide-react";

// A simple traditional arithmetic calculator (digits, + − × ÷, %, =).
const fmtCalc = (n) => {
  if (!isFinite(n)) return "Error";
  // trim floating-point noise, keep up to 12 significant digits
  return String(parseFloat(n.toPrecision(12)));
};

const compute = (a, b, op) => {
  switch (op) {
    case "+": return a + b;
    case "-": return a - b;
    case "*": return a * b;
    case "/": return b === 0 ? NaN : a / b;
    default: return b;
  }
};

const CalcKey = ({ label, onClick, variant = "num", span, testid, active }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`h-14 rounded-lg text-lg font-semibold select-none transition-colors ${
      span === 2 ? "col-span-2" : ""
    } ${
      variant === "op"
        ? active
          ? "bg-[#0047AB] text-white"
          : "bg-[#0047AB]/10 text-[#0047AB] hover:bg-[#0047AB]/20"
        : variant === "eq"
        ? "bg-[#0047AB] text-white hover:bg-[#003380]"
        : variant === "fn"
        ? "bg-slate-200 text-slate-700 hover:bg-slate-300"
        : "bg-slate-100 text-slate-900 hover:bg-slate-200"
    }`}
  >
    {label}
  </button>
);

export const BasicCalculator = ({ onValueChange }) => {
  const [display, setDisplay] = useState("0");
  const [acc, setAcc] = useState(null);
  const [op, setOp] = useState(null);
  const [waiting, setWaiting] = useState(false);

  const isError = display === "Error";

  useEffect(() => {
    onValueChange?.(display);
  }, [display, onValueChange]);

  const clearAll = () => {
    setDisplay("0");
    setAcc(null);
    setOp(null);
    setWaiting(false);
  };

  const backspace = () => {
    if (isError) return clearAll();
    if (waiting) return;
    setDisplay((d) => (d.length > 1 ? d.slice(0, -1) : "0"));
  };

  const inputDigit = (d) => {
    if (isError) {
      setDisplay(d);
      setWaiting(false);
      return;
    }
    if (waiting) {
      setDisplay(d);
      setWaiting(false);
    } else {
      setDisplay((cur) => (cur === "0" ? d : cur + d));
    }
  };

  const inputDot = () => {
    if (isError) {
      setDisplay("0.");
      setWaiting(false);
      return;
    }
    if (waiting) {
      setDisplay("0.");
      setWaiting(false);
    } else if (!display.includes(".")) {
      setDisplay((cur) => cur + ".");
    }
  };

  const toggleSign = () => {
    if (isError) return;
    setDisplay((d) => (d === "0" ? d : d.startsWith("-") ? d.slice(1) : "-" + d));
  };

  const percent = () => {
    if (isError) return;
    setDisplay((d) => fmtCalc(parseFloat(d) / 100));
  };

  const applyOp = (nextOp) => {
    if (isError) return;
    const value = parseFloat(display);
    if (op != null && !waiting) {
      const result = compute(acc, value, op);
      setAcc(result);
      setDisplay(fmtCalc(result));
    } else {
      setAcc(value);
    }
    setWaiting(true);
    setOp(nextOp);
  };

  const equals = () => {
    if (isError || op == null || acc == null) return;
    const result = compute(acc, parseFloat(display), op);
    setDisplay(fmtCalc(result));
    setAcc(null);
    setOp(null);
    setWaiting(true);
  };

  // keyboard support — ignore keystrokes while the user is typing in a form field
  const handleKey = (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const tag = (e.target?.tagName || "").toLowerCase();
    if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
    const k = e.key;
    if (k >= "0" && k <= "9") inputDigit(k);
    else if (k === ".") inputDot();
    else if (k === "+") applyOp("+");
    else if (k === "-") applyOp("-");
    else if (k === "*") applyOp("*");
    else if (k === "/") {
      e.preventDefault();
      applyOp("/");
    } else if (k === "%") percent();
    else if (k === "Enter" || k === "=") {
      e.preventDefault();
      equals();
    } else if (k === "Backspace") {
      e.preventDefault();
      backspace();
    } else if (k === "Escape" || k === "Delete") clearAll();
    else return;
  };
  const keyRef = useRef(handleKey);
  useEffect(() => {
    keyRef.current = handleKey;
  });
  useEffect(() => {
    const fn = (e) => keyRef.current(e);
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, []);

  return (
    <Card className="border-slate-200" data-testid="calculator-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold text-slate-900 uppercase tracking-wider flex items-center gap-2">
          <Calculator className="w-4 h-4 text-[#0047AB]" />
          Calculator
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="max-w-xs">
          <div
            data-testid="calc-display"
            className="mb-3 h-16 rounded-lg bg-slate-900 text-white px-4 flex items-center justify-end font-mono-clinical text-3xl font-bold overflow-x-auto"
          >
            {display}
          </div>
          <div className="grid grid-cols-4 gap-2">
            <CalcKey variant="fn" label="AC" testid="calc-clear" onClick={clearAll} />
            <CalcKey variant="fn" label="⌫" testid="calc-backspace" onClick={backspace} />
            <CalcKey variant="fn" label="%" testid="calc-percent" onClick={percent} />
            <CalcKey variant="op" label="÷" testid="calc-op-div" active={op === "/" && waiting} onClick={() => applyOp("/")} />

            <CalcKey label="7" testid="calc-key-7" onClick={() => inputDigit("7")} />
            <CalcKey label="8" testid="calc-key-8" onClick={() => inputDigit("8")} />
            <CalcKey label="9" testid="calc-key-9" onClick={() => inputDigit("9")} />
            <CalcKey variant="op" label="×" testid="calc-op-mul" active={op === "*" && waiting} onClick={() => applyOp("*")} />

            <CalcKey label="4" testid="calc-key-4" onClick={() => inputDigit("4")} />
            <CalcKey label="5" testid="calc-key-5" onClick={() => inputDigit("5")} />
            <CalcKey label="6" testid="calc-key-6" onClick={() => inputDigit("6")} />
            <CalcKey variant="op" label="−" testid="calc-op-sub" active={op === "-" && waiting} onClick={() => applyOp("-")} />

            <CalcKey label="1" testid="calc-key-1" onClick={() => inputDigit("1")} />
            <CalcKey label="2" testid="calc-key-2" onClick={() => inputDigit("2")} />
            <CalcKey label="3" testid="calc-key-3" onClick={() => inputDigit("3")} />
            <CalcKey variant="op" label="+" testid="calc-op-add" active={op === "+" && waiting} onClick={() => applyOp("+")} />

            <CalcKey variant="fn" label="±" testid="calc-sign" onClick={toggleSign} />
            <CalcKey label="0" testid="calc-key-0" onClick={() => inputDigit("0")} />
            <CalcKey label="." testid="calc-key-dot" onClick={inputDot} />
            <CalcKey variant="eq" label="=" testid="calc-equals" onClick={equals} />
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Tip: your keyboard works too — digits, + − * / % . Enter (=), Backspace, Esc (clear).</p>
        </div>
      </CardContent>
    </Card>
  );
};
